
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Check, ArrowRight, Wand } from 'lucide-react';
import { BannerTemplateFormat } from '@/types/bannerTemplate';
import BannerPreview from '@/components/admin/marketing/BannerPreview';
import { useToast } from '@/hooks/use-toast';
import { Progress } from '@/components/ui/progress';

interface BannerRejectedFixStepProps {
  rejectedBanners: BannerTemplateFormat[];
  useAI: boolean;
  onComplete: () => void;
}

const BannerRejectedFixStep: React.FC<BannerRejectedFixStepProps> = ({
  rejectedBanners,
  useAI,
  onComplete
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [aiProgress, setAiProgress] = useState(0);
  const [fixedBanners, setFixedBanners] = useState<BannerTemplateFormat[]>([]);
  const { toast } = useToast();

  const currentBanner = rejectedBanners[currentIndex];
  const progress = ((currentIndex + 1) / rejectedBanners.length) * 100;

  // Handle AI processing when useAI is enabled
  useEffect(() => {
    if (useAI && rejectedBanners.length > 0 && !isProcessing) {
      handleAIFix();
    }
  }, [useAI, rejectedBanners]);

  // Simulate AI fixing banners
  const handleAIFix = async () => {
    setIsProcessing(true);
    
    toast({
      title: "AI Processing",
      description: "AI is analyzing and fixing rejected banners...",
      variant: "default",
    });
    
    // Simulate AI processing with progress updates
    for (let i = 0; i <= 100; i += 10) {
      setAiProgress(i);
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    
    // Apply "fixes" to all rejected banners
    const fixed = rejectedBanners.map(banner => ({
      ...banner,
      status: 'approved' as const
    }));
    
    setFixedBanners(fixed);
    
    toast({
      title: "AI Processing Complete",
      description: `Successfully fixed ${rejectedBanners.length} banners.`,
      variant: "default",
    });
    
    setIsProcessing(false);
    
    // Auto-complete after a short delay
    setTimeout(() => {
      onComplete();
    }, 1500);
  };

  const handleFixCurrentBanner = () => {
    // For manual fixing, we would typically have editor controls here
    // For now, we'll just mark it as fixed
    setFixedBanners(prev => [...prev, { ...currentBanner, status: 'approved' }]);
    
    toast({
      title: "Banner Fixed",
      description: `The ${currentBanner.size.replace('_', ' ')} banner has been updated.`,
      variant: "default",
    });
    
    if (currentIndex < rejectedBanners.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      toast({
        title: "All Banners Fixed",
        description: "All rejected banners have been successfully updated.",
        variant: "default",
      });
      
      // Complete the workflow after a short delay
      setTimeout(() => {
        onComplete();
      }, 1500);
    }
  };

  const handleSkipBanner = () => {
    toast({
      title: "Banner Skipped",
      description: `The ${currentBanner.size.replace('_', ' ')} banner was skipped.`,
      variant: "default",
    });
    
    if (currentIndex < rejectedBanners.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      onComplete();
    }
  };

  if (useAI) {
    return (
      <div className="flex flex-col items-center justify-center space-y-6 py-12">
        <div className="text-center space-y-4 max-w-lg">
          <Wand className="mx-auto h-12 w-12 text-purple-500" />
          <h2 className="text-2xl font-bold">AI Banner Adjustment</h2>
          <p className="text-gray-600">
            AI is analyzing your approved banners and automatically adjusting the rejected ones to match your preferred style.
          </p>
          
          <div className="w-full space-y-2 mt-6">
            <Progress value={aiProgress} className="h-2" />
            <p className="text-sm text-right text-gray-500">{aiProgress}% complete</p>
          </div>
          
          <p className="text-sm text-gray-500 mt-6">
            {isProcessing 
              ? "Please wait while AI fixes your banners..." 
              : "All banners have been fixed!"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Fix Rejected Banners</h2>
          <p className="text-gray-500">
            Banner {currentIndex + 1} of {rejectedBanners.length}: {currentBanner?.size.replace('_', ' ')}
          </p>
        </div>
        <Progress value={progress} className="w-32 h-2" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Panel - Editor Tools (simplified for this example) */}
        <div className="lg:col-span-3">
          <div className="bg-white p-4 border rounded-md space-y-4">
            <h3 className="font-medium">Quick Adjustments</h3>
            
            <div className="space-y-2">
              <h4 className="text-sm font-medium">Positioning</h4>
              <div className="grid grid-cols-3 gap-2">
                <Button variant="outline" size="sm">Left</Button>
                <Button variant="outline" size="sm">Center</Button>
                <Button variant="outline" size="sm">Right</Button>
              </div>
            </div>
            
            <div className="space-y-2">
              <h4 className="text-sm font-medium">Size</h4>
              <div className="grid grid-cols-3 gap-2">
                <Button variant="outline" size="sm">Small</Button>
                <Button variant="outline" size="sm">Medium</Button>
                <Button variant="outline" size="sm">Large</Button>
              </div>
            </div>
            
            <div className="space-y-2">
              <h4 className="text-sm font-medium">Content Scaling</h4>
              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" size="sm">Scale Down</Button>
                <Button variant="outline" size="sm">Fit Width</Button>
              </div>
            </div>
          </div>
        </div>

        {/* Right Panel - Banner Preview */}
        <div className="lg:col-span-9 flex flex-col items-center justify-center bg-gray-50 p-6 rounded-lg">
          <div className="mb-4 w-full text-center">
            <h3 className="font-medium">Preview</h3>
            <p className="text-sm text-gray-500">Adjust this banner to fit the current size</p>
          </div>
          
          {currentBanner && (
            <div className="flex-grow flex items-center justify-center w-full">
              <BannerPreview
                size={currentBanner.size}
                backgroundColor="#ffffff"
                imageUrl={currentBanner.imageUrl}
                imageSettings={{
                  width: 100,
                  opacity: 100,
                  position: { x: 50, y: 50 },
                  isAnimated: currentBanner.isAnimated
                }}
              />
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-center space-x-4 mt-8">
        <Button variant="outline" onClick={handleSkipBanner}>
          Skip
        </Button>
        <Button onClick={handleFixCurrentBanner}>
          <Check className="mr-2 h-4 w-4" />
          Fix Banner
        </Button>
        {currentIndex < rejectedBanners.length - 1 && (
          <Button variant="secondary" onClick={() => setCurrentIndex(prev => prev + 1)}>
            Next
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        )}
      </div>

      <div className="text-center text-sm text-gray-500 mt-4">
        <p>
          Tip: Adjust the content to fit this specific banner size. Common fixes include 
          repositioning elements or adjusting text size.
        </p>
      </div>
    </div>
  );
};

export default BannerRejectedFixStep;
